import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Loader from '../../../components/Loader';
import Paginator from '../../../components/Paginator';
import ApiService from '../../../services/ApiService';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { formatDate } from '../../../utils/helper';

const ContactQueriesList = () => {
  const [isLoader, setIsLoader] = useState(false);
  const [queries, setQueries] = useState([]);
  const [pagination, setPagination] = useState({});
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);

  useEffect(() => { fetchList(); }, [page, search]);

  const fetchList = async () => {
    try {
      setIsLoader(true);
      const res = await ApiService.request({
        method: 'GET',
        url: 'admin/contactUs',
        data: { search, page, limit: 20 },
      });
      if (res.data.status) {
        setQueries(res.data.data);
        setPagination(res.data.pagination);
      } else {
        toast.error(res.data.message);
      }
    } catch (_) {}
    finally { setIsLoader(false); }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete Query?',
      text: 'This cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, delete!',
      cancelButtonColor: '#9E9B9B',
    });
    if (!result.isConfirmed) return;
    const res = await ApiService.request({ method: 'DELETE', url: `admin/contactUs/${id}` });
    if (res.data.status) { toast.success(res.data.message); fetchList(); }
    else toast.error(res.data.message);
  };

  return (
    <div className="d-flex flex-column flex-column-fluid">
      <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
        <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
          <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
            <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
              Contact Queries
            </h1>
            <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
              <li className="breadcrumb-item text-muted">
                <Link to="/admin/dashboard" className="text-muted text-hover-primary">Home</Link>
              </li>
              <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
              <li className="breadcrumb-item text-muted">Contact Queries</li>
            </ul>
          </div>
        </div>
      </div>

      <div id="kt_app_content" className="app-content flex-column-fluid">
        <div id="kt_app_content_container" className="app-container container-xxl">
          <div className="card">
            <div className="card-header border-0 pt-6">
              <div className="w-100">
                <div className="row">
                  <div className="col-md-4">
                    <input
                      type="text"
                      className="form-control form-control-solid"
                      placeholder="Search name, email, subject…"
                      value={search}
                      onChange={e => { setSearch(e.target.value); setPage(1); }}
                    />
                  </div>
                  {search && (
                    <div className="col-md-2 mt-2">
                      <button className="btn btn-sm btn-secondary" onClick={() => { setSearch(''); setPage(1); }}>
                        Clear
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="card-body pt-0">
              <div className="table-responsive">
                <table className="table align-middle table-hover fs-6 gy-5 dataTable no-footer">
                  <thead>
                    <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                      <th>#</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Subject</th>
                      <th>Received</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="fw-semibold text-gray-600 fs-6">
                    {isLoader ? (
                      <tr><td colSpan="6" className="text-center py-5"><Loader text="Loading" size={30} /></td></tr>
                    ) : queries.length === 0 ? (
                      <tr><td colSpan="6" className="text-center text-dark mt-2">No data found!</td></tr>
                    ) : queries.map((q, i) => (
                      <tr key={q._id}>
                        <td>{(page - 1) * 20 + i + 1}</td>
                        <td className="fw-bold text-dark">{q.name}</td>
                        <td>{q.email}</td>
                        <td>{q.subject || '—'}</td>
                        <td>{formatDate(q.createdAt, 'DD MMM YYYY, hh:mm A')}</td>
                        <td>
                          <button onClick={() => setSelected(q)} className="btn btn-sm h-35px btn-light-primary me-1">
                            <i className="bi bi-eye-fill fs-6"></i>
                          </button>
                          <button onClick={() => handleDelete(q._id)} className="btn btn-sm h-35px btn-light-danger">
                            <i className="bi bi-trash-fill fs-6"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <span className="text-muted fs-7">Total Records: {pagination?.total_records || 0}</span>

              {pagination.total_pages > 1 && (
                <Paginator
                  page={pagination.current_page}
                  rows={pagination.per_page}
                  totalRecords={pagination.total_records}
                  onPageChange={setPage}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {selected && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 1050 }}
            onClick={() => setSelected(null)}
          />
          <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1055 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h3 className="modal-title">Contact Query</h3>
                  <button type="button" className="btn-close" onClick={() => setSelected(null)}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="text-gray-500 fs-7 text-uppercase fw-bold">Name</label>
                    <div className="fw-semibold text-dark">{selected.name}</div>
                  </div>
                  <div className="mb-3">
                    <label className="text-gray-500 fs-7 text-uppercase fw-bold">Email</label>
                    <div className="fw-semibold text-dark">{selected.email}</div>
                  </div>
                  <div className="mb-3">
                    <label className="text-gray-500 fs-7 text-uppercase fw-bold">Subject</label>
                    <div className="fw-semibold text-dark">{selected.subject || '—'}</div>
                  </div>
                  <div className="mb-3">
                    <label className="text-gray-500 fs-7 text-uppercase fw-bold">Message</label>
                    <div className="fw-semibold text-dark" style={{ whiteSpace: 'pre-wrap' }}>{selected.message}</div>
                  </div>
                  <div>
                    <label className="text-gray-500 fs-7 text-uppercase fw-bold">Received</label>
                    <div className="fw-semibold text-dark">{formatDate(selected.createdAt, 'DD MMM YYYY, hh:mm A')}</div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-light btn-sm" onClick={() => setSelected(null)}>Close</button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ContactQueriesList;
